import { DialogCloseEvent, DialogComponent, DialogOpenEvent } from '@fc/dialog';
import {
  FCEvents,
  CartAddPendingClearEvent,
  CartAddPendingEvent,
  CartSectionsRenderedEvent,
  getCartEventData,
  isCartAddPendingEvent,
  isCartAddSuccessEvent,
} from '@fc/events';
import { cartPerformance } from '@fc/metrics';

const SHOPIFY_SECTION_PREFIX = 'shopify-section-';
const PENDING_TIMEOUT_MS = 12000;

class CartDrawerComponent extends DialogComponent {
  #openMetricDepth = 0;
  #pendingAdd = null;
  #pendingClearTimer = null;

  connectedCallback() {
    super.connectedCallback();
    this.addManagedEventListener(document, CartAddPendingEvent.eventName, this.#handleCartAddPending);
    this.addManagedEventListener(document, CartAddPendingClearEvent.eventName, this.#handleCartAddPendingClear);
    this.addManagedEventListener(document, FCEvents.cartFail, this.#handleCartAddPendingClear);
    this.addManagedEventListener(document, CartSectionsRenderedEvent.eventName, this.#handleCartSectionsRendered);
    this.addManagedEventListener(this, DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.addManagedEventListener(this, DialogCloseEvent.eventName, this.#handleDialogClose);
    this.#syncTriggerExpanded();
  }

  disconnectedCallback() {
    this.#clearPendingState({ closeIfOpened: false, reason: 'cart-drawer-disconnect' });
    super.disconnectedCallback();
  }

  #handleCartAddPending = (event) => {
    if (!this.hasAttribute('auto-open')) return;
    if (!isCartAddPendingEvent(event)) return;

    const data = getCartEventData(event);
    const wasOpen = this.refs.dialog?.open === true;
    this.#pendingAdd = {
      operationId: this.#operationIdFromEvent(event),
      sourceId: event.detail?.sourceId || null,
      openedByDrawer: !wasOpen,
    };
    this.#setPendingState(true, data);
    this.#schedulePendingFallback();
    this.#openAndBindInstallments('add:drawer-pending-open', { reason: 'cart-add-pending' });
  };

  #handleCartAddPendingClear = (event) => {
    if (!this.#pendingAdd) return;
    if (!this.#matchesPendingOperation(event)) return;

    const data = getCartEventData(event);
    this.#clearPendingState({
      closeIfOpened: true,
      reason: data.aborted ? 'cart-add-abort' : data.didError ? 'cart-add-error' : 'cart-add-clear',
    });
  };

  #handleCartSectionsRendered = (event) => {
    if (!this.hasAttribute('auto-open')) return;
    if (!isCartAddSuccessEvent(event)) return;
    if (!this.#matchesRenderedSection(event)) return;
    if (!this.#matchesPendingOperation(event)) return;

    this.#clearPendingState({ closeIfOpened: false, reason: 'cart-add-rendered' });
    this.#openAndBindInstallments('add:drawer-open', { reason: 'cart-add-rendered' });
  };

  open() {
    this.#openAndBindInstallments('drawer:open', { reason: 'manual-open' });
  }

  showDialog(detail = {}) {
    return cartPerformance.measure(this.#openMetricDepth > 0 ? null : 'drawer:open', () => this.#showDialog(detail));
  }

  #showDialog(detail = {}) {
    super.showDialog(detail);

    if (this.refs.dialog?.open) {
      this.#syncTriggerExpanded(true);
      this.#bindInstallmentsCloseAction();
    }
  }

  close() {
    this.closeDialog();
  }

  #handleDialogOpen = () => {
    this.#syncTriggerExpanded(true);
  };

  #handleDialogClose = () => {
    this.#syncTriggerExpanded(false);
  };

  #openAndBindInstallments(measureName = null, detail = {}) {
    this.#openMetricDepth += 1;
    try {
      return cartPerformance.measure(measureName, () => this.#showDialog(detail));
    } finally {
      this.#openMetricDepth -= 1;
    }
  }

  #operationIdFromEvent(event) {
    return getCartEventData(event).cartAddOperationId || null;
  }

  #matchesPendingOperation(event) {
    if (!this.#pendingAdd) return true;

    const operationId = this.#operationIdFromEvent(event);
    if (this.#pendingAdd.operationId && operationId) {
      return this.#pendingAdd.operationId === operationId;
    }

    const sourceId = event.detail?.sourceId || null;
    return !this.#pendingAdd.sourceId || !sourceId || this.#pendingAdd.sourceId === sourceId;
  }

  #setPendingState(isPending, data = {}) {
    this.toggleAttribute('data-cart-add-pending', isPending);

    const dialog = this.refs.dialog;
    if (dialog instanceof HTMLElement) {
      if (isPending) {
        dialog.setAttribute('aria-busy', 'true');
      } else {
        dialog.removeAttribute('aria-busy');
      }
    }

    const cartItems = this.querySelector('cart-items');
    if (cartItems instanceof HTMLElement) {
      if (isPending) {
        cartItems.setAttribute('aria-busy', 'true');
      } else {
        cartItems.removeAttribute('aria-busy');
      }
    }

    const pendingState = this.refs.pendingState;
    if (pendingState instanceof HTMLElement) {
      pendingState.hidden = !isPending;
    }

    const pendingMessage = this.refs.pendingMessage;
    if (isPending && pendingMessage instanceof HTMLElement) {
      pendingMessage.textContent = this.#pendingMessage(data);
    }
  }

  #pendingMessage() {
    const loading = window.FitCheck?.translations?.loading;
    return typeof loading === 'string' && loading.trim() ? loading : 'Loading...';
  }

  #schedulePendingFallback() {
    if (this.#pendingClearTimer) {
      clearTimeout(this.#pendingClearTimer);
    }

    this.#pendingClearTimer = setTimeout(() => {
      this.#pendingClearTimer = null;
      this.#clearPendingState({ closeIfOpened: false, reason: 'cart-add-pending-timeout' });
    }, PENDING_TIMEOUT_MS);
  }

  #clearPendingState({ closeIfOpened = false, reason = 'cart-add-settled' } = {}) {
    if (this.#pendingClearTimer) {
      clearTimeout(this.#pendingClearTimer);
      this.#pendingClearTimer = null;
    }

    const pendingAdd = this.#pendingAdd;
    this.#pendingAdd = null;
    this.#setPendingState(false);

    if (closeIfOpened && pendingAdd?.openedByDrawer && this.refs.dialog?.open) {
      void this.closeDialog({ reason });
    }
  }

  #matchesRenderedSection(event) {
    const renderedSectionId = event.detail?.sectionId;
    if (!renderedSectionId) return true;

    const sectionIds = this.#renderSectionIds();
    return sectionIds.size ? sectionIds.has(renderedSectionId) : true;
  }

  #renderSectionIds() {
    const sectionIds = new Set();
    const cartItems = this.querySelector('cart-items[data-section-id]');

    if (cartItems instanceof HTMLElement && cartItems.dataset.sectionId) {
      sectionIds.add(cartItems.dataset.sectionId);
    }

    const section = this.closest('.shopify-section');
    const id = section instanceof HTMLElement ? section.id : '';
    if (id) {
      sectionIds.add(id.startsWith(SHOPIFY_SECTION_PREFIX) ? id.slice(SHOPIFY_SECTION_PREFIX.length) : id);
    }

    return sectionIds;
  }

  #syncTriggerExpanded(isExpanded = this.refs.dialog?.open === true) {
    const trigger = this.refs.cartDrawerTrigger;
    if (!(trigger instanceof HTMLElement)) return;

    trigger.setAttribute('aria-expanded', String(Boolean(isExpanded)));
  }

  #bindInstallmentsCloseAction() {
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = document
        .querySelector('shopify-payment-terms')
        ?.shadowRoot?.querySelector('#shopify-installments-cta');

      if (!cta) return;
      cta.addEventListener('click', () => this.closeDialog(), { once: true });
    });
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawerComponent);
}
